import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Para onde a RD devolve o navegador. Tem que estar cadastrado no app da RD, igual, letra por letra. */
export const RD_STATION_REDIRECT_PATH = '/integracoes/rdstation/callback';
export const RD_STATION_AUTH_MESSAGE = 'lemeai:rdstation-auth';
export const RD_STATION_STATE_KEY = 'lemeai:rdstation-oauth-state';

export const montarRdStationRedirectUri = () => `${window.location.origin}${RD_STATION_REDIRECT_PATH}`;

export interface RdStationEntrada {
    /** "oportunidade" = contato + oportunidade na pipeline; "contato" = só o cadastro. */
    acao: 'oportunidade' | 'contato';
    /** "distribuir" = a mesma régua das conversas novas; "usuario" = sempre a mesma pessoa. */
    responsavel: 'distribuir' | 'usuario';
    usuario_id: number | null;
}

export interface RdStationSaida {
    /** Oportunidade que nasce no CRM (WhatsApp, manual…) vira conversão na RD. */
    conversao: boolean;
    /** Saiu de qualificação para negociação: "Lead Qualificado" e oportunidade na RD. */
    oportunidade: boolean;
    venda: boolean;
    perdida: boolean;
    /** Campo cf_ da conta RD que recebe o nome da etapa a cada mudança. null = não envia. */
    campo_etapa: string | null;
}

export interface RdStationCampoMapeado {
    rd: string;
    /** null = não trazer. */
    campo_personalizado_id: number | null;
}

export interface RdStationConexao {
    appConfigurado: boolean;
    conectada: boolean;
    conexaoPlataformaId: number | null;
    conta: string | null;
    status: number | null;
    conectadaEm: string | null;
    entrada: RdStationEntrada;
    saida: RdStationSaida;
    campos: RdStationCampoMapeado[];
    webhookAtivo: boolean;
    webhookErro: string | null;
    recebidosHoje: number;
    oportunidadesHoje: number;
    ultimoEvento: string | null;
}

export interface RdStationCampo {
    identificador: string;
    nome: string;
    tipoDado: string;
    personalizado: boolean;
}

interface Resposta<T> { sucesso: boolean; mensagem: string; dados?: T }

/* Lê o corpo como texto antes do JSON: rota inexistente na API responde 404 VAZIO, e um
   `res.json()` direto estoura com mensagem de parser em vez de dizer o que houve. */
const chamar = async <T>(caminho: string, init?: RequestInit): Promise<Resposta<T>> => {
    try {
        const res = await apiFetch(`${API_URL}/api/rdstation/${caminho}`, init);
        const texto = await res.text();
        if (texto) {
            try {
                const json = JSON.parse(texto) as Resposta<T>;
                return { sucesso: res.ok && json.sucesso !== false, mensagem: json.mensagem ?? '', dados: json.dados };
            } catch { /* corpo que não é JSON: cai na mensagem por status */ }
        }
        if (res.status === 404) return { sucesso: false, mensagem: 'A API não tem este recurso. Ela pode estar desatualizada.' };
        if (res.status === 401) return { sucesso: false, mensagem: 'Sua sessão expirou. Entre de novo.' };
        if (res.status === 403) return { sucesso: false, mensagem: 'Só o administrador da empresa pode fazer isto.' };
        return { sucesso: res.ok, mensagem: res.ok ? '' : 'Não foi possível falar com o servidor.' };
    } catch {
        return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
    }
};

const json = (method: string, corpo?: unknown): RequestInit => ({
    method,
    headers: { 'Content-Type': 'application/json' },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
});

export const RdStationService = {
    urlAutorizacao: (state: string) =>
        chamar<{ urlAutorizacao: string }>(`UrlAutorizacao?${new URLSearchParams({ redirectUri: montarRdStationRedirectUri(), state })}`),

    /** Envia o code de uso único. O token fica na API e nunca passa pelo navegador. */
    conectar: (code: string) => chamar<RdStationConexao>('Conectar', json('POST', { code })),

    obter: () => chamar<RdStationConexao>('Conexao'),

    salvar: (r: { entrada: RdStationEntrada; saida: RdStationSaida; campos: RdStationCampoMapeado[] }) =>
        chamar<RdStationConexao>('Configuracao', json('PUT', r)),

    campos: () => chamar<RdStationCampo[]>('Campos'),

    reativarEntrada: () => chamar<RdStationConexao>('ReativarEntrada', json('POST')),

    desconectar: () => chamar<null>('Conexao', { method: 'DELETE' }),
};
